import type { FC } from 'react';
import { ArrowUpRight, Mail, Sparkles } from 'lucide-react';
import {
  CLEANING_EXTRA_OPTIONS,
  CLEANING_FREQUENCY_LABELS,
  CLEANING_TYPE_LABELS,
  formatUsd,
  MAPLE_MOP_QUOTE_URL,
  type CleaningFrequency,
  type CleaningType,
} from '../../../../../shared/maple-mop-estimate.ts';
import type { A2uiNodeViewProps } from '@/app/lib/a2ui/catalog.tsx';
import { arr, num, str } from '@/app/lib/a2ui/props.ts';
import { isRecord } from '@/app/lib/util/type-guards.ts';

interface QuoteLine {
  id: string;
  label: string;
  detail?: string;
  amount: number;
}

function readLines(value: unknown): QuoteLine[] {
  return arr(value)
    .filter(isRecord)
    .map((line) => ({
      id: str(line.id),
      label: str(line.label),
      detail: typeof line.detail === 'string' ? line.detail : undefined,
      amount: num(line.amount) ?? 0,
    }))
    .filter((line) => line.id.length > 0 && line.label.length > 0);
}

function isCleaningType(value: string): value is CleaningType {
  return value === 'standard' || value === 'deep' || value === 'move-out';
}

function isFrequency(value: string): value is CleaningFrequency {
  return (
    value === 'one-time' ||
    value === 'weekly' ||
    value === 'every-2-weeks' ||
    value === 'monthly'
  );
}

function Detail({ label, value }: { label: string; value: string }) {
  return (
    <div className="min-w-0 border-t border-border/70 pt-3">
      <dt className="mb-1 text-base text-muted-foreground">{label}</dt>
      <dd className="break-words text-base font-medium text-foreground">{value}</dd>
    </div>
  );
}

export const MapleMopQuote: FC<A2uiNodeViewProps> = ({ node, renderChildren }) => {
  if (node.children.length === 0) return null;
  return <div className="@container/quote w-full min-w-0">{renderChildren()}</div>;
};

export const CleaningQuoteCard: FC<A2uiNodeViewProps> = ({ node }) => {
  const homeType = str(node.props.homeType);
  const bedrooms = num(node.props.bedrooms);
  const bathrooms = num(node.props.bathrooms);
  const type = str(node.props.cleaningType);
  const frequency = str(node.props.frequency);
  const extras = arr(node.props.extras).filter((value): value is string => typeof value === 'string');
  const notes = str(node.props.notes).trim();
  const needsFollowUp = node.props.needsFollowUp === true;
  const preferredDay = str(node.props.preferredDay).trim();
  const timeSlot = str(node.props.timeSlot);
  const firstName = str(node.props.firstName).trim();
  const email = str(node.props.email).trim();
  const total = num(node.props.estimatedTotal);
  const lines = readLines(node.props.lineItems);
  const breakdownId = `${node.id}-breakdown`;
  const detailsId = `${node.id}-details`;

  if (
    !homeType ||
    bedrooms === undefined ||
    bathrooms === undefined ||
    !isCleaningType(type) ||
    !isFrequency(frequency) ||
    total === undefined ||
    lines.length === 0
  ) {
    return null;
  }

  const selectedExtras = CLEANING_EXTRA_OPTIONS.filter((extra) => extras.includes(extra.id));
  const hasQuoteDetails = Boolean(
    preferredDay &&
      (timeSlot === 'morning' || timeSlot === 'afternoon') &&
      firstName &&
      /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email),
  );
  const showSummary = Boolean(
    preferredDay || firstName || email || timeSlot === 'morning' || timeSlot === 'afternoon',
  );

  return (
    <article className="w-full overflow-hidden rounded-lg border border-border bg-card text-card-foreground shadow-elevated">
      <div className="p-5 @sm/quote:p-7">
        <div className="mb-5 flex items-start gap-3">
          <span className="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-accent text-accent-foreground">
            <Sparkles className="h-5 w-5" aria-hidden="true" />
          </span>
          <div className="min-w-0 flex-1">
            <p className="text-base font-medium text-muted-foreground">Maple &amp; Mop Cleaning</p>
            <h2 className="mt-1 text-balance text-2xl font-semibold tracking-tight text-foreground">
              Your cleaning estimate
            </h2>
          </div>
        </div>

        <div className="mb-6 rounded-md bg-accent/70 px-4 py-4">
          <p className="text-base font-medium text-accent-foreground">Estimated total</p>
          <p className="mt-1 text-3xl font-semibold tabular-nums tracking-tight text-accent-foreground">
            {formatUsd(total)}
          </p>
          <p className="mt-2 text-base leading-relaxed text-accent-foreground">
            Final price is confirmed on the quote page.
          </p>
        </div>

        <section aria-labelledby={breakdownId} className="mb-6">
          <h3 id={breakdownId} className="mb-2 text-lg font-semibold text-foreground">
            Itemized breakdown
          </h3>
          <ul className="divide-y divide-border border-y border-border">
            {lines.map((line) => (
              <li key={line.id} className="flex min-w-0 items-start justify-between gap-4 py-3">
                <div className="min-w-0 flex-1">
                  <p className="break-words text-base font-medium text-foreground">{line.label}</p>
                  {line.detail ? (
                    <p className="mt-0.5 break-words text-base leading-relaxed text-muted-foreground">
                      {line.detail}
                    </p>
                  ) : null}
                </div>
                <span className="shrink-0 pt-0.5 text-base font-medium tabular-nums text-foreground">
                  {formatUsd(line.amount)}
                </span>
              </li>
            ))}
          </ul>
          <p className="mt-3 text-base leading-relaxed text-muted-foreground">
            The frequency discount is applied after selected extras.
          </p>
        </section>

        <section aria-labelledby={detailsId} className="mb-6">
          <h3 id={detailsId} className="mb-3 text-lg font-semibold text-foreground">
            {showSummary ? 'Your quote details' : 'Based on your home'}
          </h3>
          <dl className="grid grid-cols-1 gap-x-6 gap-y-3 @sm/quote:grid-cols-2">
            <Detail label="Home type" value={homeType} />
            <Detail label="Bedrooms" value={String(bedrooms)} />
            <Detail label="Bathrooms" value={String(bathrooms)} />
            <Detail label="Cleaning" value={CLEANING_TYPE_LABELS[type]} />
            <Detail label="Frequency" value={CLEANING_FREQUENCY_LABELS[frequency]} />
            <Detail
              label="Extras"
              value={selectedExtras.length ? selectedExtras.map((extra) => extra.label).join(', ') : 'None'}
            />
            {notes ? <Detail label="Notes for the crew" value={notes} /> : null}
            {showSummary && preferredDay ? <Detail label="Preferred day" value={preferredDay} /> : null}
            {showSummary && (timeSlot === 'morning' || timeSlot === 'afternoon') ? (
              <Detail
                label="Preferred time"
                value={timeSlot === 'morning' ? 'Morning' : 'Afternoon'}
              />
            ) : null}
            {showSummary && firstName ? <Detail label="First name" value={firstName} /> : null}
            {showSummary && email ? <Detail label="Email" value={email} /> : null}
          </dl>
        </section>

        {needsFollowUp ? (
          <p className="mb-6 flex items-start gap-2 rounded-md border border-border bg-muted px-4 py-3 text-base leading-relaxed text-foreground">
            <Mail className="mt-0.5 h-5 w-5 shrink-0 text-primary" aria-hidden="true" />
            <span>The team can confirm any accommodation or pricing impact by email.</span>
          </p>
        ) : null}

        {showSummary ? (
          <p className="mb-4 rounded-md border border-border bg-muted px-4 py-3 text-base leading-relaxed text-muted-foreground">
            Your preferred day and time are requests, not a confirmed or reserved slot.
          </p>
        ) : null}

        {hasQuoteDetails ? (
          <div className="space-y-3">
            <a
              className="inline-flex min-h-12 w-full items-center justify-center gap-2 rounded-md bg-primary px-5 py-3 text-base font-semibold text-primary-foreground transition-[filter] hover:brightness-90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[3px] focus-visible:outline-primary touch-manipulation"
              href={MAPLE_MOP_QUOTE_URL}
              target="_blank"
              rel="noreferrer"
            >
              Get my exact quote
              <ArrowUpRight className="h-4 w-4" aria-hidden="true" />
            </a>
            <p className="text-center text-base leading-relaxed text-muted-foreground">
              The quote page confirms the final price and handles booking.
            </p>
          </div>
        ) : null}
      </div>

      <footer className="border-t border-border bg-muted/70 px-5 py-3 text-sm text-muted-foreground @sm/quote:px-7">
        Demo company. Prices are examples.
      </footer>
    </article>
  );
};
